/**
 * Public-safe copy for ApiError.message construction (no ApiError import — avoids
 * cycles with ApiClient / resolveApiErrorFromHttpResponse).
 */
import { pickLocalizedApiMessage } from 'pi-kiosk-shared';
import { isCommerceBlockedErrorCode } from 'pi-kiosk-shared/eet/CommerceBlockedErrorCode';
import i18n from '../../i18n.js';
import { donationErrorMessage } from '../api/errorCodes.js';

const DEVELOPER_MESSAGE_PATTERNS: readonly RegExp[] = [
  /\bJSON\.?\s*parse\b/i,
  /\bUnexpected token\b/i,
  /\bInvalid JSON\b/i,
  /\bRequest failed\b/i,
  /\bHTTP\s*\d{3}\b/i,
  /\(\s*[45]\d{2}\s*\)/,
  /\bNetwork error\b/i,
  /\bFailed to fetch\b/i,
  /\bLoad failed\b/i,
  /\bTypeError\b/i,
  /\bSyntaxError\b/i,
  /\bECONNREFUSED\b/i,
  /\bENOTFOUND\b/i,
  /\bETIMEDOUT\b/i,
  /\bECONNRESET\b/i,
  /\bAbortError\b/i,
  /\bCORS\b/,
  /\bCSRF\b/i,
  /\bInternal Server Error\b/i,
  /\bBad Gateway\b/i,
  /\bService Unavailable\b/i,
  /\bGateway Timeout\b/i,
  /^\s*Unauthorized\s*$/i,
  /^\s*Forbidden\s*$/i,
  /^\s*Unauthenticated\s*$/i,
  /^\s*Access Denied\s*$/i,
  /^\s*Not Found\s*$/i,
  /^\s*Bad Request\s*$/i,
  /\bRate limited\b/i,
  /\bToo many requests\b/i,
  /\battempt ceiling\b/i,
  /\bat\s+\S+\s*\(/,
  /\bUNEXPECTED[_A-Z0-9]+\b/,
  /\bINVALID_JSON\b/i,
  /\bprisma\b/i,
  /\bstack(trace)?\b/i,
  /^\s*Error:\s/i,
  /\bHTTP storm\b/i,
  /\bStorm guard\b/i,
  /\bfair queue overflow\b/i,
  /\bAPI response missing data\b/i,
  /\bCustomer \/me\b/i,
  /\bgetConditional\b/i,
  // Field-validation boilerplate (never public scan/shop UI copy).
  // G11 — intentional global hardening: any API using resolvePublicApiFailureMessage
  // filters these phrases, not scan-only — avoids raw backend validation on EN locale.
  /\bcannot be empty\b/i,
  /\bmust be between\b/i,
  /\bInvalid GTIN\b/i,
  /\bBarcode contains\b/i,
  /\bBarcode length must\b/i,
  /\bpayload cannot be empty\b/i,
  /\bcannot be resolved as product barcodes\b/i,
  /\bPayment SPAYD payloads cannot\b/i,
  /\bLoyalty cards cannot be used\b/i,
  /\bCustomer card payloads cannot\b/i,
  // G6 — missing-cred protocol sentinel / infra (never show in UI)
  /\bhttpOnly cookie\b/i,
  /\bmissing customer session credential\b/i,
  /\bsession credential\b/i,
  // HYG-G15 — EET / fiscal leakage (never show in UI)
  /\bCOMMERCE_BLOCKED\b/,
  /\bEI[ČC]\b/,
  /\bPOK\b/,
  /\bEET\b/,
];

/** Bare English infra when the UI locale is Czech/Slovak (not EN/pseudo). */
const RAW_ENGLISH_WHEN_UI_NOT_EN: readonly RegExp[] = [
  /^\s*Unauthorized\s*$/i,
  /^\s*Forbidden\s*$/i,
  /^\s*Unauthenticated\s*$/i,
  /^\s*Access Denied\s*$/i,
  /^\s*Rate limited\s*$/i,
  /^\s*Too many requests\s*$/i,
  /^\s*Request failed\s*$/i,
  /^\s*Network error\s*$/i,
  /^\s*Not Found\s*$/i,
  /^\s*Bad Request\s*$/i,
  /^\s*Internal Server Error\s*$/i,
  /^\s*Storm guard rejected request\s*$/i,
];

function tCustomer(key: string): string {
  return i18n.t(key, { ns: 'customer' });
}

function uiLocaleIsEnglish(): boolean {
  const lng = (i18n.resolvedLanguage ?? i18n.language ?? 'cs').toLowerCase();
  return lng.startsWith('en') || lng === 'pseudo';
}

/** Czech + Slovak diacritics — presence means localized public copy, not EN leak. */
const CS_SK_DIACRITICS_RE =
  /[áäčďéěíĺľňóôŕšťúůýžÁÄČĎÉĚÍĹĽŇÓÔŔŠŤÚŮÝŽ]/;

/**
 * Distinctive CS/SK public-UI ASCII tokens (no diacritics). Allow path only —
 * never an English hint list. Shared EN loanwords are listed separately and
 * cannot alone mark Latin prose as safe ("Invalid email" stays unsafe).
 */
const CS_SK_ASCII_LEXICON = new Set([
  'chyba',
  'zadejte',
  'zadej',
  'nebo',
  'telefon',
  'telefonni',
  'heslo',
  'heslem',
  'prosim',
  'zkuste',
  'znovu',
  'email',
  'mail',
  'kod',
  'kodu',
  'overovaci',
  'overit',
  'overeni',
  'spatne',
  'spatny',
  'spravne',
  'prihlas',
  'prihlaste',
  'prihlasit',
  'prihlaseni',
  'prihlasovaci',
  'odhlas',
  'ucet',
  'uctu',
  'objednavka',
  'objednavky',
  'platba',
  'platby',
  'pokracovat',
  'zrusit',
  'ulozit',
  'nacist',
  'nenalezeno',
  'povinne',
  'neplatne',
  'neplatny',
  'platny',
  'platna',
  'uspesne',
  'dekujeme',
  'vitejte',
  'nastaveni',
  'souhlas',
  'podminky',
  'ochrana',
  'udaju',
  'adresa',
  'adresu',
  'jmeno',
  'prijmeni',
  'cislo',
  'cisla',
  'castka',
  'castku',
  'celkem',
  'kosik',
  'kosiku',
  'vybrat',
  'zpet',
  'dalsi',
  'dokoncete',
  'dokoncit',
  'odeslat',
  'odeslano',
  'poslat',
  'smazat',
  'upravit',
  'pridat',
  'odebrat',
  'hledat',
  'obchod',
  'obchodu',
  'prodejna',
  'mapa',
  'pohled',
  'ulozene',
  'oblibene',
  'pockejte',
  'sekund',
  'minut',
  'hodin',
  'neco',
  'pokazilo',
  'akci',
  'musite',
  'docasne',
  'nedostupna',
  'nedostupne',
  'sluzba',
  'vyplnte',
  'zkontrolujte',
  'kontaktujte',
  'podporu',
  'podpora',
  'pozdeji',
  'chvili',
  'nepodarilo',
  'dostupne',
  'volitelne',
  'formatu',
  'kiosku',
  'prodejniho',
  'prodejnim',
  'miste',
  'vyzvednuti',
  'polozky',
  'kladnou',
  'uctenka',
  'uctenku',
  'uzivatelske',
  'sms',
  'otp',
  'pin',
]);

/** Shared EN/CS tokens — insufficient alone to treat Latin prose as CS/SK copy. */
const CS_SK_ASCII_LOANWORDS = new Set([
  'email',
  'mail',
  'sms',
  'otp',
  'pin',
  'url',
  'qr',
  'id',
  'http',
  'https',
  'api',
]);

function collectStringLeaves(
  value: unknown,
  exact: Set<string>,
  lower: Set<string>,
): void {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return;
    }
    exact.add(trimmed);
    lower.add(trimmed.toLowerCase());
    return;
  }
  if (value !== null && typeof value === 'object') {
    for (const child of Object.values(value as Record<string, unknown>)) {
      collectStringLeaves(child, exact, lower);
    }
  }
}

let cachedPublicCopyLocale: string | null = null;
let cachedPublicCopyExact: Set<string> | null = null;
let cachedPublicCopyLower: Set<string> | null = null;

function normalizeCustomerBundleLocale(lng: string): string {
  const lower = lng.toLowerCase();
  if (lower.startsWith('sk')) {
    return 'sk';
  }
  if (lower.startsWith('en')) {
    return 'en';
  }
  if (lower === 'pseudo') {
    return 'pseudo';
  }
  return 'cs';
}

/** Public strings from the active customer i18n bundle (exact + lower). */
function knownPublicCustomerCopySets(): {
  exact: Set<string>;
  lower: Set<string>;
} {
  const normalized = normalizeCustomerBundleLocale(
    i18n.resolvedLanguage ?? i18n.language ?? 'cs',
  );
  if (
    cachedPublicCopyLocale === normalized &&
    cachedPublicCopyExact !== null &&
    cachedPublicCopyLower !== null
  ) {
    return { exact: cachedPublicCopyExact, lower: cachedPublicCopyLower };
  }
  const exact = new Set<string>();
  const lower = new Set<string>();
  collectStringLeaves(i18n.getResourceBundle(normalized, 'customer'), exact, lower);
  cachedPublicCopyLocale = normalized;
  cachedPublicCopyExact = exact;
  cachedPublicCopyLower = lower;
  return { exact, lower };
}

function isKnownPublicCustomerCopy(trimmed: string): boolean {
  const { exact, lower } = knownPublicCustomerCopySets();
  return exact.has(trimmed) || lower.has(trimmed.toLowerCase());
}

function tokenizeLatinWords(trimmed: string): string[] {
  const normalized = trimmed.toLowerCase().replace(/e-mail/g, 'email');
  return normalized.match(/[a-z]{2,}/g) ?? [];
}

/**
 * True when ASCII Latin text looks like CS/SK public copy without diacritics.
 * Requires ≥1 distinctive (non-loanword) lexicon token. Loanwords alone
 * never suffice — blocks "SMS OTP", "Invalid email or PIN", "Email required".
 */
function looksLikeCsSkAsciiPublicCopy(trimmed: string): boolean {
  const tokens = tokenizeLatinWords(trimmed);
  if (tokens.length === 0) {
    return false;
  }
  const lexiconHits = tokens.filter((token) => CS_SK_ASCII_LEXICON.has(token));
  if (lexiconHits.length === 0) {
    return false;
  }
  const distinctiveHits = lexiconHits.filter(
    (token) => CS_SK_ASCII_LOANWORDS.has(token) === false,
  );
  return distinctiveHits.length >= 1;
}

/**
 * When UI is cs/sk, true if `message` is unmapped Latin English prose.
 * EN/pseudo always returns false.
 *
 * G1 policy — default-deny Latin ASCII on non-EN UI:
 * 1. CS/SK diacritics → safe
 * 2. other non-ASCII → not an EN API leak
 * 3. known customer-namespace i18n values (exact / case-insensitive) → safe
 * 4. CS/SK ASCII lexicon (distinctive public words) → safe
 * 5. otherwise ≥4 Latin letters → unsafe (never show raw EN sentence)
 */
export function looksLikeNonUiLocaleEnglish(message: string): boolean {
  if (uiLocaleIsEnglish()) {
    return false;
  }
  const trimmed = message.trim();
  if (trimmed.length === 0) {
    return false;
  }
  if (CS_SK_DIACRITICS_RE.test(trimmed)) {
    return false;
  }
  // Non-ASCII without CS/SK diacritics (other scripts / umlauts) — not an EN API leak.
  if (/[\u0080-\uFFFF]/.test(trimmed)) {
    return false;
  }
  const letterChars = trimmed.match(/[A-Za-z]/g) ?? [];
  if (letterChars.length < 4) {
    return false;
  }
  if (isKnownPublicCustomerCopy(trimmed)) {
    return false;
  }
  if (looksLikeCsSkAsciiPublicCopy(trimmed)) {
    return false;
  }
  // Unmapped Latin-script prose on cs/sk — including hint-word-free EN sentences.
  return true;
}

/** True when the string is unsafe to show as public UI copy for the current locale. */
export function isUnsafePublicApiMessage(message: string): boolean {
  return (
    looksLikeDeveloperErrorMessage(message) || looksLikeNonUiLocaleEnglish(message)
  );
}

/**
 * True when `message` looks like developer / infra jargon unsuitable for the public UI.
 */
export function looksLikeDeveloperErrorMessage(message: string): boolean {
  const trimmed = message.trim();
  if (trimmed.length === 0) {
    return true;
  }
  if (DEVELOPER_MESSAGE_PATTERNS.some((re) => re.test(trimmed))) {
    return true;
  }
  // Bare SCREAMING_SNAKE API codes mistaken for UI copy
  if (/^[A-Z][A-Z0-9_]{2,}$/.test(trimmed)) {
    return true;
  }
  // camelCase API identifiers shown as the whole message (e.g. validationError)
  if (/^[a-z][a-zA-Z0-9]*[A-Z][a-zA-Z0-9]*$/.test(trimmed) && trimmed.length < 80) {
    return true;
  }
  if (!uiLocaleIsEnglish() && RAW_ENGLISH_WHEN_UI_NOT_EN.some((re) => re.test(trimmed))) {
    return true;
  }
  // Slash-joined multi-locale leftovers that pickLocalizedApiMessage could not reduce
  if (
    trimmed.includes(' / ') &&
    /\b(Invalid|Unauthorized|Forbidden|HTTP|JSON|Error|Request failed)\b/i.test(trimmed)
  ) {
    return true;
  }
  return false;
}

/** Status-based public fallback (network / auth / rate / 5xx / generic). */
export function publicMessageForHttpStatus(status: number | undefined): string {
  if (status === 401 || status === 403) {
    return tCustomer('errors.auth.title');
  }
  if (status === 429) {
    return tCustomer('errors.rateLimited');
  }
  if (status !== undefined && status >= 500) {
    return tCustomer('errors.temporaryUnavailable');
  }
  if (status === 0) {
    return tCustomer('errors.network.title');
  }
  return tCustomer('errors.generic');
}

function isNetworkStyleMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('failed to fetch') ||
    lower.includes('network error') ||
    lower.includes('load failed') ||
    lower.includes('econnrefused') ||
    lower.includes('enotfound') ||
    lower.includes('etimedout') ||
    lower.includes('networkerror')
  );
}

/**
 * Resolve a customer-safe ApiError.message from status/code/optional raw text.
 * Prefer known donation/auth code maps; never keep developer jargon.
 */
export function resolvePublicApiFailureMessage(input: {
  status: number;
  code?: string;
  rawMessage?: string;
}): string {
  const code = input.code;

  // Known codes first (including status 0 ME_CONDITIONAL_FORBIDDEN).
  if (code === 'NETWORK_ERROR') {
    return tCustomer('errors.network.title');
  }
  if (code === 'TENANT_INACTIVE') {
    return tCustomer('errors.temporaryUnavailable');
  }
  if (
    code === 'HTTP_STORM_DETECTED' ||
    code === 'HTTP_STORM_LATCHED' ||
    code === 'CUSTOMER_OUTBOUND_QUEUE_OVERFLOW'
  ) {
    return tCustomer('errors.rateLimited');
  }
  // G4 — empty/platform tenant on membership OIDC start (no infra paragraph).
  if (code === 'TENANT_REQUIRED') {
    return tCustomer('errors.auth.title');
  }
  if (
    code === 'INVALID_JSON' ||
    code === 'MISSING_DATA' ||
    code === 'ME_TRANSIENT' ||
    code === 'ME_ATTEMPT_CEILING' ||
    code === 'ME_CONDITIONAL_FORBIDDEN'
  ) {
    return tCustomer('errors.temporaryUnavailable');
  }
  if (code === 'BARCODE_INVALID_FORMAT') {
    return tCustomer('shop.scannerInvalidCode');
  }
  if (isCommerceBlockedErrorCode(code)) {
    return tCustomer('shop.payDisabled.paymentsUnavailable');
  }
  if (code !== undefined && code.length > 0) {
    const unmappedSentinel = '__UNMAPPED_CUSTOMER_ERROR__';
    const mapped = donationErrorMessage(code, unmappedSentinel);
    if (mapped !== unmappedSentinel) {
      return mapped;
    }
  }

  const localized = pickLocalizedApiMessage(
    input.rawMessage ?? '',
    i18n.resolvedLanguage ?? i18n.language,
  ).trim();

  // G6 — protocol sentinel is for isMissingCredentialUnauthorized detection only
  // (compare ApiError.message to MISSING_CUSTOMER_SESSION_CREDENTIAL_MESSAGE).
  // Never return it as UI copy — would expose "httpOnly cookie" infra text.
  if (
    localized.toLowerCase().includes('missing customer session credential') ||
    localized.toLowerCase().includes('httponly cookie')
  ) {
    return tCustomer('errors.auth.title');
  }

  if (isNetworkStyleMessage(localized) || input.status === 0) {
    return tCustomer('errors.network.title');
  }
  if (localized.length > 0 && isUnsafePublicApiMessage(localized) === false) {
    return localized;
  }
  return publicMessageForHttpStatus(input.status);
}
